/**
 * @file     leadConversion.ts
 * @readme   ../CLAUDE.md
 * @purpose  Single source of truth for lead-conversion tracking. Maps lead types to value tiers
 *           and fires one GA4 `generate_lead_<category>` event per submission, pinned to the
 *           GA4 measurement id. Google Ads conversions come via the GA4 -> Ads import of that
 *           event, never from a gtag call that reaches the Ads tag.
 *           Meta Lead is reported from here too, including when the GA4 server leg
 *           owns the conversion. The Pixel has no server backstop.
 *           Every form submission funnels through here, do not inline gtag calls elsewhere.
 */
'use client';

// Tier table + LeadType / ConversionTier exports live in a shared, server-safe module
// so server route handlers can resolve a tier without dragging this client module.
import { env } from '@/core/config/env';
import { trackMetaLead } from './metaPixel';
import {
  LEAD_TYPE_TO_TIER,
  TIERS,
  getLeadConversionValue,
  getLeadEventName,
  type LeadType,
  type ConversionTier,
} from './leadConversionTiers';

export { getLeadConversionValue, getLeadEventName, LEAD_TYPE_TO_TIER, TIERS };
export type { LeadType, ConversionTier };

export interface LeadConversionContext {
  /** Short identifier of the originating flow (e.g. 'sellerFlow', 'buyerFlow'). */
  flowId?: string;
  /** Page the form was submitted from. */
  sourcePage?: string;
  /** FUB/DB identifier once the server has responded. */
  referenceId?: string;
  /** Optional override of the computed value (e.g. high-value seller with appraisal). */
  valueOverride?: number;
  /** Landing page that originated the lead, used as a GA4 custom dimension. */
  landingSlug?: string;
  /** Experiment sub-variant id chosen on that landing page. */
  landingVariant?: string;
  /** Landing-page intent classification. */
  landingIntent?: string;
  /** BCP-47 locale the page was viewed in. */
  landingLocale?: string;
}

const CURRENCY = 'CAD';

function readRelatedAttributionCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|; )relta_gclid=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function gtag(...args: unknown[]) {
  if (typeof window === 'undefined') return;
  window.gtag?.(...args);
}

// In-memory dedup ledger: transaction_id → timestamp of last emit. Protects against
// React re-renders, double submits, or anything else that calls trackLeadConversion
// twice for the same lead in the same tab. 5-minute window is long enough to cover
// realistic duplicate scenarios without blocking legitimate later events.
// GA4 and Meta keep separate ledgers so a Meta-only emit does not suppress the
// GA4 hit, and calling both entry points for one reference still sends one Lead.
const DEDUP_WINDOW_MS = 5 * 60 * 1000;
const recentlyFired = new Map<string, number>();
const recentlyFiredMeta = new Map<string, number>();

function seenRecently(ledger: Map<string, number>, transactionId: string): boolean {
  const now = Date.now();
  // Garbage collect stale entries
  for (const [id, ts] of ledger) {
    if (now - ts > DEDUP_WINDOW_MS) ledger.delete(id);
  }
  const last = ledger.get(transactionId);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return true;
  ledger.set(transactionId, now);
  return false;
}

function alreadyFired(transactionId: string): boolean {
  return seenRecently(recentlyFired, transactionId);
}

/**
 * Meta `Lead` for one consumer submission. Independent of which leg reports
 * GA4: there is no Meta server path, so a server-leg lead still calls this.
 * `referenceId` is the eventID a later Conversions API leg can dedupe on.
 * Agent recruiting stays out. Never throws.
 */
export function trackMetaLeadConversion(type: LeadType, ctx: LeadConversionContext = {}): void {
  try {
    if (typeof window === 'undefined' || type === 'agent-enquiry') return;

    const { value: tieredValue } = getLeadConversionValue(type);
    const value = ctx.valueOverride ?? tieredValue;
    const transactionId =
      ctx.referenceId ??
      `client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    if (seenRecently(recentlyFiredMeta, transactionId)) return;

    // Force-load the pixel (and gtag) if the visitor converts before interacting.
    window.dispatchEvent(new Event('relta:lead-submitted'));
    trackMetaLead({ eventId: transactionId, value, currency: CURRENCY, leadType: type });
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[leadConversion] meta lead failed:', err);
    }
  }
}

/**
 * Fires the lead-submission conversion events on the client.
 *
 * - Dispatches a `relta:lead-submitted` DOM event so the gtag loader can force-load
 *   if the user converts before scrolling/clicking.
 * - Fires GA4 `generate_lead_<category>` with tiered value (Google Ads counts it via the GA4 import).
 *
 * Never throws, analytics failures must not block lead UX.
 */
export function trackLeadConversion(type: LeadType, ctx: LeadConversionContext = {}): void {
  try {
    if (typeof window === 'undefined') return;

    const { tier, value: tieredValue } = getLeadConversionValue(type);
    const value = ctx.valueOverride ?? tieredValue;
    // Agent recruiting reports under its own category so the consumer
    // campaigns' Ads import (and their smart bidding) never sees a B2B signup.
    const eventName = getLeadEventName(type);
    const flowId = ctx.flowId;
    const sourcePage = ctx.sourcePage;

    // Transaction ID ties the browser leg to the lead record and gives Google Ads
    // a dedup key of its own. Always use the server's referenceId when present,
    // else fall back to a generated id so the event still goes through
    // (attribution lost but counted once).
    const transactionId =
      ctx.referenceId ??
      `client-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    // In-tab dedup: suppress if the same transaction_id fired within the last 5 minutes.
    if (alreadyFired(transactionId)) return;

    // Force-load the gtag script (no-op if already loaded or listener absent).
    window.dispatchEvent(new Event('relta:lead-submitted'));

    // GA4 only. `send_to` pins the event to the measurement id: without it gtag
    // fans every event out to EVERY configured target, so the Google Ads tag
    // (AW-…, configured in GtagBoot for remarketing) received
    // `generate_lead` directly on top of the GA4 -> Ads import of the same
    // event, giving Ads two independent sources for one conversion.
    //
    // `event_id` rides along for the server leg's benefit only. GA4 does not
    // deduplicate anything but `purchase`, and gtag strips `event_id` before the
    // request leaves the browser, so the two legs are kept mutually exclusive at
    // the source instead (see gtagDelivery.ts).
    // `first_touch_gclid` is reporting-only; kept here because GA4 dimensions can use it.
    const firstTouchGclid = readRelatedAttributionCookie();

    const sharedParams = {
      ...(env.gaMeasurementId ? { send_to: env.gaMeasurementId } : {}),
      currency: CURRENCY,
      value,
      lead_type: type,
      tier,
      flow_id: flowId,
      source_page: sourcePage,
      transaction_id: transactionId,
      event_id: transactionId,
      first_touch_gclid: firstTouchGclid,
      landing_slug: ctx.landingSlug,
      landing_variant: ctx.landingVariant,
      landing_intent: ctx.landingIntent,
      landing_locale: ctx.landingLocale,
    };

    gtag('event', eventName, sharedParams);

    // Meta Lead uses the same transaction id. Server-leg callers that skip this
    // function report it through trackMetaLeadConversion instead.
    trackMetaLeadConversion(type, { ...ctx, referenceId: transactionId, valueOverride: value });

    // Google Ads conversions are NOT fired directly from here. They come via the
    // GA4 -> Google Ads import of `generate_lead` (configured in the Ads UI). The
    // old client-side `gtag('event','conversion', {send_to})` was removed: it was
    // client-only with no server backstop, so it was lost whenever the lazily
    // downloaded gtag library had not flushed before the page navigated away,
    // which is why Ads under-counted. The server MP leg now feeds the import too.
  } catch (err) {
    // Swallow, analytics must never break lead UX.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[leadConversion] track failed:', err);
    }
  }
}

